/**
 * Live Azure catalog (read-only dropdown data) — kinds, built-in fallbacks and the
 * defaults the Create-VM form starts from (contract v2 §1 / §3).
 *
 * The backend answers every catalog call with HTTP 200 and `source: "fallback"` when
 * Azure is unreachable; the wizard additionally falls back to the built-in items
 * below when the request itself fails, so the form stays usable.
 */
import type {
  AppriseImageRef, AzureCatalogItems, AzureCatalogKind, AzureCatalogParams, AzureImage, AzureLocation,
  AzureSecurityTypeOption, AzureSubscriptionInfo, AzureVmSize,
} from '@/types'

/** Debounce for the image / size search boxes. */
export const SEARCH_DEBOUNCE_MS = 300

// ─── Defaults from the Azure portal "Create a virtual machine" screenshots ────

export const DEFAULT_SUBSCRIPTION_NAME = 'Apprise-ERP-RnD'
export const DEFAULT_RESOURCE_GROUP = 'rg-eastus2-rnd-apprise-shared'
export const DEFAULT_VM_NAME = 'dapprisetask01'
/** Display name as stored in the wizard's azure_region (the backend also accepts the code "eastus2"). */
export const DEFAULT_REGION = 'East US 2'
export const DEFAULT_ADMIN_USERNAME = 'cloudops_admin'
export const DEFAULT_VM_SIZE = 'Standard_D2s_v3'
export const DEFAULT_IMAGE: AppriseImageRef = {
  publisher: 'MicrosoftWindowsServer', offer: 'WindowsServer', sku: '2025-datacenter-g2', version: 'latest',
}
export const DEFAULT_IMAGE_LABEL = 'Windows Server 2025 Datacenter - x64 Gen2'
export const DEFAULT_VNET_ADDRESS_SPACE = '172.16.0.0/16'
export const DEFAULT_SUBNET_PREFIX = '172.16.0.0/24'

// ─── Catalog helpers ──────────────────────────────────────────────────────────

/** Drops empty params so the cache key and the URL stay stable. */
export function cleanCatalogParams(params: AzureCatalogParams | undefined): AzureCatalogParams {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(params ?? {})) if (typeof v === 'string' && v.trim()) out[k] = v.trim()
  return out
}

export interface CatalogResult<T> {
  items: T[]
  source: 'azure' | 'fallback'
  /** Why the built-in defaults are shown (source === 'fallback'). */
  error: string
  isLoading: boolean
  isFetching: boolean
}

export const EMPTY_CATALOG: CatalogResult<never> = { items: [], source: 'azure', error: '', isLoading: false, isFetching: false }

/** "Could not load from Azure: <error> — using built-in defaults" (the exact visible note). */
export function fallbackNote(error: string): string {
  return `Could not load from Azure: ${error.trim() || 'unknown error'} — using built-in defaults`
}

// ─── Built-in fallbacks (the §1 defaults) ─────────────────────────────────────

const win = (label: string, sku: string, offer = 'WindowsServer'): AzureImage => ({
  publisher: 'MicrosoftWindowsServer', offer, sku, version: 'latest', label, os_type: 'windows', hyperv_generation: 'V2', architecture: 'x64',
})

export const FALLBACK_IMAGES: AzureImage[] = [
  win(DEFAULT_IMAGE_LABEL, '2025-datacenter-g2'),
  win('Windows Server 2022 Datacenter: Azure Edition - x64 Gen2', '2022-datacenter-azure-edition'),
  win('Windows Server 2019 Datacenter - x64 Gen2', '2019-datacenter-gensecond'),
  { publisher: 'Canonical', offer: '0001-com-ubuntu-server-jammy', sku: '22_04-lts-gen2', version: 'latest',
    label: 'Canonical / 0001-com-ubuntu-server-jammy / 22_04-lts-gen2', os_type: 'linux', hyperv_generation: 'V2', architecture: 'x64' },
  { publisher: 'RedHat', offer: 'RHEL', sku: '9-lvm-gen2', version: 'latest',
    label: 'RedHat / RHEL / 9-lvm-gen2', os_type: 'linux', hyperv_generation: 'V2', architecture: 'x64' },
]

const size = (name: string, vcpus: number, memory_gb: number): AzureVmSize => ({
  name, vcpus, memory_gb, max_data_disks: 4 * vcpus, zones: ['1', '2', '3'], restrictions: [], hyperv_generations: ['V1', 'V2'],
  encryption_at_host_supported: true, trusted_launch_disabled: false, available: true, price_month_usd: null, price_note: '',
})

export const FALLBACK_SIZES: AzureVmSize[] = [
  size(DEFAULT_VM_SIZE, 2, 8), size('Standard_D4s_v3', 4, 16), size('Standard_D2s_v5', 2, 8), size('Standard_D4s_v5', 4, 16),
]

/** Built-in regions when Azure cannot be reached: only the regions the portal currently allows. */
export const FALLBACK_LOCATIONS: AzureLocation[] = [
  { name: 'australiasoutheast', display_name: 'Australia Southeast', supports_zones: false },
  { name: 'canadacentral', display_name: 'Canada Central', supports_zones: true },
  { name: 'centralus', display_name: 'Central US', supports_zones: true },
  { name: 'eastus', display_name: 'East US', supports_zones: true },
  { name: 'eastus2', display_name: 'East US 2', supports_zones: true },
  { name: 'northcentralus', display_name: 'North Central US', supports_zones: false },
  { name: 'southcentralus', display_name: 'South Central US', supports_zones: true },
  { name: 'uksouth', display_name: 'UK South', supports_zones: true },
  { name: 'westus', display_name: 'West US', supports_zones: false },
  { name: 'westus2', display_name: 'West US 2', supports_zones: true },
]

export const FALLBACK_SECURITY_TYPES: AzureSecurityTypeOption[] = [
  { id: 'standard', supported: true },
  { id: 'trusted_launch', supported: false, reason: 'Could not be checked against Azure' },
  { id: 'confidential', supported: false, reason: 'Not supported by the portal yet' },
]

const FALLBACKS: { [K in AzureCatalogKind]: AzureCatalogItems[K][] } = {
  'resource-groups': [{ name: DEFAULT_RESOURCE_GROUP, location: 'eastus2' }],
  locations: FALLBACK_LOCATIONS,
  'availability-options': [{ id: 'none', label: 'No infrastructure redundancy required' }],
  'security-types': FALLBACK_SECURITY_TYPES,
  images: FALLBACK_IMAGES,
  sizes: FALLBACK_SIZES,
  'virtual-networks': [],
  'public-ips': [],
  'network-security-groups': [],
  'load-balancers': [],
  'storage-accounts': [],
}

/** Built-in items for a catalog kind (filtered by `q` for the searchable ones). */
export function fallbackItems<K extends AzureCatalogKind>(kind: K, q = ''): AzureCatalogItems[K][] {
  const items = FALLBACKS[kind]
  const needle = q.trim().toLowerCase()
  if (!needle) return items
  if (kind === 'images') return (items as AzureImage[]).filter((i) => `${i.label} ${i.publisher} ${i.offer} ${i.sku}`.toLowerCase().includes(needle)) as AzureCatalogItems[K][]
  if (kind === 'sizes') return (items as AzureVmSize[]).filter((s) => s.name.toLowerCase().includes(needle)) as AzureCatalogItems[K][]
  return items
}

export const PATCH_MODE_OPTIONS: ReadonlyArray<{ id: 'manual' | 'automatic_by_os' | 'automatic_by_platform' | 'image_default'; label: string; hint: string }> = [
  { id: 'manual', label: 'Manual updates', hint: 'You install updates yourself (the Azure portal default for this form).' },
  { id: 'automatic_by_os', label: 'Automatic by OS (Windows Automatic Updates)', hint: 'Windows Update installs updates according to the OS configuration.' },
  { id: 'automatic_by_platform', label: 'Azure-orchestrated (automatic by platform)', hint: 'Azure Update Manager orchestrates patching in safe, availability-aware batches.' },
  { id: 'image_default', label: 'Image default', hint: 'Use whatever the image defaults to.' },
]

/** Keep only the allowed regions (codes). No list / an empty list = no limit. The API enforces the same limit. */
export function restrictLocations(locations: AzureLocation[], allowed: readonly string[] | undefined): AzureLocation[] {
  const codes = (allowed ?? []).map((c) => c.toLowerCase().replace(/[^a-z0-9]/g, '')).filter(Boolean)
  return codes.length === 0 ? locations : locations.filter((l) => codes.includes(l.name.toLowerCase()))
}

/** Azure location code for the wizard's region value ("East US 2" or "eastus2" → "eastus2"). */
export function locationCodeFor(region: string, locations: AzureLocation[]): string {
  const r = region.trim().toLowerCase()
  const hit = locations.find((l) => l.display_name.toLowerCase() === r || l.name.toLowerCase() === r)
  return hit?.name ?? r.replace(/[^a-z0-9]/g, '')
}

/**
 * Why the pinned subscription blocks the form ('' = no blocker). A subscription that cannot
 * be read at all is NOT a blocker here — the backend refuses plan / apply / verify itself.
 */
export function subscriptionBlocker(info: AzureSubscriptionInfo | undefined, optionsPinned?: boolean): string {
  const notPinned =
    'No Azure subscription is pinned for Apprise (APPRISE_AZURE_SUBSCRIPTION is not set). Creating a VM is blocked until an administrator pins the target subscription.'
  if (info) {
    if (!info.pinned) return notPinned
    if (info.error) return `The pinned subscription could not be resolved: ${info.error}. Creating a VM is blocked until an administrator fixes it.`
    if (!info.cli_default_matches)
      return `The Azure CLI default subscription does not match the pinned subscription "${info.name}". Creating a VM is blocked until an administrator fixes the server's Azure sign-in.`
    return ''
  }
  return optionsPinned === false ? notPinned : ''
}

/** One-line label for an image: friendly label + the exact reference. */
export function imageRefString(ref: AppriseImageRef): string {
  return `${ref.publisher}:${ref.offer}:${ref.sku}:${ref.version}`
}

export function sameImage(a: AppriseImageRef, b: AppriseImageRef): boolean {
  return a.publisher === b.publisher && a.offer === b.offer && a.sku === b.sku && a.version === b.version
}

/** OS of an image reference when the catalog item is not at hand (Microsoft Windows Server images only). */
export function guessOsType(ref: AppriseImageRef): 'windows' | 'linux' {
  return /windows/i.test(ref.publisher) || /windows/i.test(ref.offer) ? 'windows' : 'linux'
}
